import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { KpiTile } from "@/components/reporting/KpiTile.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  ReportingBarTable,
  ReportingBarTableRow,
} from "@/pages/reporting/components/ReportingBarTable.tsx";
import { RecruitingReportingRecruitersSelector } from "@/pages/reporting/recruiting/components/RecruitingReportingRecruitersSelector.tsx";
import { DisqualificationReasonsReportTable } from "@/pages/reporting/recruiting/disqualification-reasons/DisqualificationReasonsReportTable.tsx";
import { useRecruitingReports } from "@/pages/reporting/recruiting/recruiting-reports.hook.ts";
import { useRecruitAnalyticsDisqualificationReasonsReport } from "@/queries/analytics/use-recruit-analytics.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAnalyticsRecruitingFilter } from "@/types/analytics/analytics.recruiting.types.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { Box, Page, Stack, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const DisqualificationReasonsReport = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { handlePreviewApplicants } = useRecruitingReports();

  useTitle(t("ats:report_disqualification_reasons_title"), t("common:reports"));

  const library = filtersLibrary();
  const {
    filter: { filter },
    config,
    filterButton,
  } = useAppFilter<ApiAnalyticsRecruitingFilter>({
    filters: [
      {
        ...library.date_from,
        hideInFilter: true,
        name: "date_start",
      },
      {
        ...library.date_to,
        hideInFilter: true,
        name: "date_end",
      },
      {
        ...library.period,
        defaultValue: "last_month_0",
      },
      {
        ...library.employee_ids,
        name: "action_owner_ids",
        label: t("core:people_with_activity"),
      },
      {
        ...library.employee_ids,
        name: "recruiter_ids",
        label: t("ats:recruiters_involved"),
      },
      library.job_ids,
      {
        ...library.employee_ids,
        name: "stakeholder_ids",
        label: t("ats:stakeholders"),
      },
      {
        ...library.disqualification_reason,
        name: "disqualification_reason_ids",
        label: t("ats:disqualification_reason"),
      },
    ],
    source: AnalyticReportTypes.recruitingDisqualificationReasons,
  });
  const { data: report, isLoading } = useRecruitAnalyticsDisqualificationReasonsReport(filter);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.recruitingDisqualificationReasons,
    });
  });

  const disqualifiedCount = useMemo(
    () =>
      report?.report.rows.reduce(
        (acc, row) => acc + row.reasons.reduce((sum, item) => sum + item.value, 0),
        0
      ) ?? 0,
    [report]
  );

  const calculatedReportRows = useMemo(() => {
    if (!report) return [];

    const rows: ReportingBarTableRow[] = [];

    for (const reason of report.config.reasons) {
      const totalByReason = report.report.rows.reduce((acc, row) => {
        const item = row.reasons.find((e) => e.reason.id == reason.id);
        return acc + (item ? item.value : 0);
      }, 0);
      if (totalByReason == 0) continue;

      const applicantIdsByReason = report.report.rows.flatMap((row) => {
        const item = row.reasons.find((e) => e.reason.id == reason.id);
        return item ? item.applicants_ids : [];
      }, []);
      rows.push({
        title: reason.title,
        cells: [
          {
            value: totalByReason,
            title: `${totalByReason} (${Math.round((totalByReason / disqualifiedCount) * 100)}%)`,
            entity_ids: applicantIdsByReason,
          },
        ],
      });
    }

    return rows.sort((a, b) => b.cells[0].value - a.cells[0].value);
  }, [report, disqualifiedCount]);

  return (
    <>
      <Box className={"bg-light h-full overflow-y-auto"}>
        <Page.Header title={t("ats:report_disqualification_reasons_title")}>
          <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
            <FilterChoosePeriod filtersConfig={config} />
            {filterButton()}
          </Stack>
        </Page.Header>

        <Page.Content>
          <Box>
            <RecruitingReportingRecruitersSelector
              recruiterIds={filter.recruiter_ids || []}
              onChange={(recruiterIds) => {
                setSearchParams((searchParams) => {
                  searchParams.set("recruiter_ids", recruiterIds.join(","));
                  return searchParams;
                });
              }}
            />
          </Box>
          <Box className={"grid md:grid-cols-4 gap-(--gap-md) pt-(--gap-lg)"}>
            <KpiTile
              title={t("ats:report_disqualification_reason_total_candidates")}
              value={disqualifiedCount}
              format={"integer"}
              isLoading={isLoading}
            />
          </Box>
          <Title
            header={t("ats:report_disqualification_reason_table_title")}
            caption={t("ats:report_disqualification_reason_table_description")}
            paddingTop
            paddingBottom
          />

          <DisqualificationReasonsReportTable report={report} isLoading={isLoading} />
          <Title
            header={t("ats:report_disqualification_reason_totals_table_title")}
            paddingTop
            paddingBottom={true}
            size={"xs"}
          />
          <ReportingBarTable
            rows={calculatedReportRows}
            onClick={(_, cell) => {
              if (cell.entity_ids) {
                handlePreviewApplicants(
                  cell.entity_ids,
                  AnalyticReportTypes.recruitingDisqualificationReasons
                );
              }
            }}
          />
        </Page.Content>
      </Box>
    </>
  );
};
